// Imports for external libraries go here.
import clsx from 'clsx';
import { useRef, useEffect, useState } from 'react';
// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.

import { Button, Heading, Types, RichText } from '@marriott/mi-ui-library';
import { StyledStickyCardSection } from './StickyCardSection.styles';
import { StickyCardSectionProps } from './StickyCardSection.types';
import { CobrandImage } from '../../../molecules/CobrandImage/CobrandImage';

import { COLUMN_COUNT, VERTICAL } from '../../../modules/constants';
import { extractAnnualFee, replaceWhitespaceAndAddBtn } from '../../../modules/utils';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useSession } from '@marriott/mi-headless-utils';
import { isAuthenticated } from '@marriott/mi-ui-library';

export const StickyCardSectionConfig = {
  emptyLabel: 'StickyCardSection',
  isEmpty: () => true,
  resourceType: `/components/content/StickyCardSection`,
};

export const StickyCardSection = (props: StickyCardSectionProps) => {
  const {
    componentId,
    imagePath,
    cardTitle,
    altText,
    linkText,
    linkUrl,
    openInaNewTab,
    columnCount,
    hideButtonOnMobile,
    variant = 'horizontal',
    header,
    pointSectionDescription,
    tertiaryLinks,
    showCtaButton,
    isAmexButton,
    isChaseButton,
    isTertiaryLinksVisible,
    isImagehidden,
    isPointDescriptionVisible,
    isAnnualFeeVisible,
    trackingProperties,
    pricingTermsGuest = '',
    pricingTermsSignedIn = '',
  } = props;
  const { datalayer } = useSession();
  const linkType = openInaNewTab ? 'external' : 'internal';
  const ref = useRef<HTMLDivElement>(null);
  const chaseRef = useRef<HTMLDivElement>(null);
  const [pricingUrl, setPricingUrl] = useState<string>(pricingTermsGuest);
  const newHeader =
    variant && variant === 'vertical' ? cardTitle : cardTitle?.replace('t-subtitle-m', 't-label-alt-xs');

  useEffect(() => {
    if (datalayer) {
      setPricingUrl(isAuthenticated() ? pricingTermsSignedIn : pricingTermsGuest);
    }
  }, [datalayer]);
  return (
    <StyledStickyCardSection className={clsx('d-flex justify-content-center')}>
      <div
        className={clsx(
          columnCount === '1' ? 'justify-content-center mx-2' : columnCount === '2' ? 'mr-lg-5 pr-lg-4 mr-0' : '',
          'd-flex',
          'sticky-card',
          variant === 'vertical' ? 'd-flex flex-column align-items-center' : ''
        )}
      >
        {!isImagehidden && (
          <CobrandImage
            defaultImageURL={imagePath}
            altText={altText}
            customClass={clsx('sticky-card-image', variant === VERTICAL && 'mt-md-1 sticky-card-image--large')}
          />
        )}

        <div
          className={clsx(
            columnCount === '1'
              ? 'mx-lg-5 mx-md-3 mx-2'
              : columnCount === '2'
              ? 'mx-lg-4 ml-2 mr-md-3'
              : variant === 'vertical'
              ? 'mx-lg-1'
              : 'mx-lg-3 ml-2 mr-md-3',
            'd-flex flex-wrap',
            variant === 'vertical' ? 'justify-content-center text-center' : 'flex-column align-items-baseline'
          )}
        >
          {variant === 'vertical' && header && (
            <Heading
              titleText={header}
              variation={Types.headingType.title}
              fontSize={Types.size.medium}
              customClass={clsx('d-inline d-md-none text-center mt-4')}
            />
          )}

          <div
            dangerouslySetInnerHTML={{
              __html: isPointDescriptionVisible ? pointSectionDescription || '' : newHeader,
            }}
            className={clsx(
              variant === 'vertical'
                ? 'my-3 mb-md-2 mx-2 mx-lg-0 col-12'
                : (columnCount === '1' ? 'mb-2' : columnCount === '2' ? 'mb-2' : 'mb-md-3 mb-2') +
                    ' mt-0 mt-lg-2 sticky-card-heading' +
                    isPointDescriptionVisible && 'no-border-top-bottom flex-grow-1'
            )}
          />

          {isAnnualFeeVisible && (
            <RichText text={extractAnnualFee(header) || ''} componentId="annual-fee-message" customClass="pt-2" />
          )}
          {pricingUrl && <RichText text={pricingUrl} componentId="pricing-terms" customClass="mb-md-2" />}

          {isTertiaryLinksVisible && (
            <div
              dangerouslySetInnerHTML={{ __html: tertiaryLinks || '' }}
              className={clsx(
                variant === 'vertical'
                  ? 'my-3 mb-md-2 mx-2 mx-lg-0 col-12'
                  : (columnCount === '1' ? 'mb-2' : columnCount === '2' ? 'mb-2' : 'mb-md-3 mb-2') +
                      'mt-0 mt-3 sticky-card-links'
              )}
            />
          )}
          {(isAmexButton || isChaseButton || showCtaButton) && (
            <div className={clsx(isAmexButton && 'mt-auto')}>
              <Button
                id={replaceWhitespaceAndAddBtn(componentId)}
                isLink
                href={linkUrl}
                ref={ref}
                trackingProperties={trackingProperties}
                target={openInaNewTab === 'true' ? '_blank' : '_self'}
                className={clsx('d-md-inline-block', isAmexButton ? 'mt-3 m-button-s' : 'd-none mt-auto m-button-m')}
                linkType={linkType}
                buttonCopy={linkText}
                linkAriaLabelOpenNewWindow={openInaNewTab ? 'Open in new tab' : ''}
              ></Button>
            </div>
          )}

          {(isChaseButton || showCtaButton) && (
            <Button
              id={`${componentId}-btn`}
              isLink={true}
              href={linkUrl}
              ref={chaseRef}
              trackingProperties={trackingProperties}
              target={openInaNewTab === 'true' ? '_blank' : '_self'}
              className={clsx(
                'm-link-tertiary-button-external m-link-action',
                hideButtonOnMobile ? 'd-none' : 'd-inline d-md-none',
                'pb-1 mb-1 mt-1 mt-auto mr-0'
              )}
              linkType={linkType}
              buttonCopy={linkText}
              linkAriaLabelOpenNewWindow={openInaNewTab ? 'Open in new tab' : ''}
            ></Button>
          )}
        </div>
      </div>
    </StyledStickyCardSection>
  );
};
